import React, { FC } from 'react';
import { AppBundle } from 'api/optimalprint-sdk.d';
import getSelectedBundleItems from 'utils/bundles/getSelectedBundleItems';
import { Accordion } from 'components/Common';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { deselectBundleProductAction, selectBundleProductAction } from 'store/publicAssetProduct/slice';
import OpCarousel from 'components/Common/op-ui-wrappers/OpCarousel/OpCarousel';
import OpMountableFrame from 'components/Common/op-ui-wrappers/OpMountableFrame/OpMountableFrame';
import getCurrentProduct from 'store/publicAssetProduct/selector/getCurrentProduct';
import BundleProductItem from '../StudioBundleProductItem/StudioBundleProductItem';
import styles from './StudioBundledProductsGroup.module.scss';

export interface BundledProduct {
  bundleGroupId: number;
  productId: number;
  categoryId: number;
}

export interface StudioBundledProductsGroupProps {
  bundleGroup: AppBundle.Api.Entity.Design.V1.DesignAbstractProduct.DesignProductItem.IBundleGroup;
  allSelectedProducts: BundledProduct[];
}

const StudioBundledProductsGroup: FC<StudioBundledProductsGroupProps> = ({ bundleGroup, allSelectedProducts }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const product = useSelector(getCurrentProduct);

  const selectedProducts = allSelectedProducts
    .filter((sp) => sp.bundleGroupId === bundleGroup.bundleGroupId)
    .map((sp) => sp.productId);
  if (selectedProducts.length === 0) {
    // preselect none option
    selectedProducts.push(0);
  }
  const selectedItems = getSelectedBundleItems(bundleGroup, selectedProducts);

  const toggleBundleProduct = (productItem: AppBundle.Api.Entity.App.V1.AbstractProduct.IProductItem) => {
    const isSelected = selectedProducts.indexOf(productItem.productId) !== -1;
    if (!isSelected) {
      dispatch(selectBundleProductAction({ bundleGroup, productItem }));
    } else {
      dispatch(deselectBundleProductAction({ bundleGroupId: bundleGroup.bundleGroupId, productItem }));
    }
  };

  const displayFrameDescription = () => {
    if (selectedItems && selectedItems.length && selectedItems[0].productId > 0) {
      return (
        <div className={styles.selectedItemDetails}>
          <Accordion
            expanded
            title={`${selectedItems[0].name} ${t('txt_description')}`}
            titleClassName={styles.descriptionTitle}
          >
            <div className={styles.description}>{(selectedItems[0] as any).content.productDescription}</div>
            <OpMountableFrame
              product-uid={product?.productUid}
              bundled-product-uid={selectedItems ? selectedItems[0].productUid : ''}
            />
          </Accordion>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.bundleGroup}>
      <div className={styles.groupTitle}>{bundleGroup.label}</div>
      <OpCarousel variant="frames-selector">
        {bundleGroup.items?.map((item, index) => (
          <BundleProductItem
            key={`bpi${index}`}
            bundleGroupId={bundleGroup.bundleGroupId}
            item={item}
            selected={selectedProducts.indexOf(item.productId) !== -1}
            onClick={toggleBundleProduct}
          />
        ))}
      </OpCarousel>
      {displayFrameDescription()}
    </div>
  );
};

export default StudioBundledProductsGroup;
